/* eslint-disable no-unused-vars */
/* eslint-disable indent */
import PropTypes from 'prop-types';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Tooltip } from 'primereact/tooltip';
import { useLocation } from 'react-router-dom';
import DownloadScientificProfile from 'components/DownloadScientificProfile';
import WarningScientificProfileUrl from 'components/WarningScientificProfileUrl';
import { ORGANIZATION_TYPES, PROJECT_ROLES, TIMEZONE_VN, currencyOptions } from 'constant';
import { getAcadamicLevelShortHand, is1593 } from 'utils/func';

import RichTextViewer from 'components/RichTextViewer';
import { useGetPersonnelAdjustmentByProjectId } from 'utils/hooks/personnelAdjustments';
import DomesticExpertFields from './DomesticExpertFields';
import InternationalExpertFields from './InternationalExpertFields';
import MainParticipantFields from './MainParticipantFields';
import OwnerFields from './OwnerFields';
import OwnerSection from './OwnerSection';
import ParticipantFields from './ParticipantFields';
import RecommendedExpertsFields from './RecommendedExpertsFields';
import ScientificSecretaryFields from './ScientificSecretaryFields';
import SecretarySection from './SecretarySection';
import TechnicianFields from './TechnicianFields';
// eslint-disable-next-line import/no-cycle
import ExpenditureSection from './ExpenditureSection';
import ViewHistoricalPersonnelAdjustment from '../../ViewHistoricalPersonnelAdjustment';

export default function GeneralInfo({
  generalInfoData,
  expenditureData,
  expenditureInGeneral,
  isLoading,
  handleDownloadFile,
  handleDownloadScientificProfileFile,
}) {
  // #region Data
  const { t } = useTranslation();
  const { state } = useLocation();
  const projectId = state?.projectId || null;

  const template = useMemo(
    () => generalInfoData?.template || parseInt(generalInfoData?.template, 10) || null,
    [generalInfoData]
  );

  const [viewHistoricalScientificProfileVisible, setViewHistoricalScientificProfileVisible] =
    useState(false);

  const owner = useMemo(
    () =>
      generalInfoData?.projectProposalContacts?.find(
        (item) => item?.projectRole?.code === PROJECT_ROLES.OWNER
      ) ?? {},
    [generalInfoData]
  );

  const partners = useMemo(
    () =>
      generalInfoData?.projectProposalOrganizations?.filter(
        (org) => org.type === ORGANIZATION_TYPES.PARTNER
      ) ?? [],
    [generalInfoData?.projectProposalOrganizations]
  );

  const { data: personnelAdjustments, isLoading: isPersonnelAdjustmentsLoading } =
    useGetPersonnelAdjustmentByProjectId(projectId, {
      enabled: !!projectId,
    });
  // #endregion

  // #region Event
  // eslint-disable-next-line react/prop-types
  const formatIndex = (value, props) => props.rowIndex + 1;

  const formatFullname = (value) => (
    <>
      {getAcadamicLevelShortHand(value.academicRank, value.academicDegree, t, true)}
      {value.fullname}
      {value?.contact?.user?.scientificProfileUrl ? (
        <DownloadScientificProfile
          contactId={value?.id || value?.contact?.id || value?.contactId}
          contactName={value?.fullname}
          academicRank={value?.academicRank}
          academicDegree={value?.academicDegree}
          handleDownloadFile={handleDownloadScientificProfileFile}
        />
      ) : (
        <WarningScientificProfileUrl />
      )}
    </>
  );

  const formatGender = (value) => (value?.gender ? t(`userProfile.gender.${value.gender}`) : '');

  const formatResearcherOrganization = (value) => {
    if (value.organization?.englishName === 'Other') return value.specificOrganizationName;
    return t('lang') === 'vi' ? value.organization?.name : value.organization?.englishName;
  };

  const formatResearcherOrganizationHistory = (value) => {
    if (value.contact.organization?.englishName === 'Other') return value.specificOrganizationName;
    return t('lang') === 'vi'
      ? value.contact.organization?.name
      : value.contact.organization?.englishName;
  };
  // #endregion Event
  return (
    <div className="pb-2">
      <h4>{t('projectProposal.general.viewLabel')}</h4>
      {/* A1 */}
      <h4 className="mb-2">{t('projectProposal.general.projectNameAndType.label')}</h4>
      <div className="ml-5 text-lg">
        <div className="flex mb-1">
          <div className="mr-2 font-semibold">
            {t('projectProposal.general.projectNameAndType.vietnameseName')}:
          </div>
          <div className="flex-1">{generalInfoData?.vietnameseName}</div>
        </div>
        <div className="flex mb-1">
          <div className="mr-2 font-semibold">
            {t('projectProposal.general.projectNameAndType.englishName')}:{' '}
          </div>
          <div className="flex-1">{generalInfoData?.englishName}</div>
        </div>
        <div className="flex mb-1">
          <div className="mr-2 font-semibold">
            {t('projectProposal.general.projectNameAndType.projectType')}:{' '}
          </div>
          <div className="flex-1">{t(`projectTypes.${generalInfoData?.projectType}`)}</div>
          {is1593(template) && (
            <>
              <div className="mr-2 font-semibold">
                {t('projectProposal.general.projectNameAndType.experimental')}:{' '}
              </div>
              <div className="flex-1">
                {generalInfoData?.isExperimental ? t('options.yes') : t('options.no')}
              </div>
            </>
          )}
        </div>
      </div>

      {/* -majorsOrMajorsGroup */}
      <h4>{t('projectProposal.general.majorsOrMajorsGroup.label')}</h4>
      {generalInfoData?.projectProposalResearchFields
        ?.sort((a, b) => {
          if (a.priority === null) return 1;
          if (b.priority === null) return -1;
          if (a.priority === b) return 0;
          return a.priority < b.priority ? -1 : 1;
        })
        .map((item, index) => (
          <div className="flex ml-5 text-lg mb-3" key={item?.researchField.id}>
            <div className="mr-2 font-semibold">
              {t('projectProposal.general.majorsOrMajorsGroup.prio.researchField')}{' '}
              {item?.priority ?? index + 1}:{' '}
            </div>
            <div className="w-20rem" style={{ marginRight: '150px' }}>
              {item?.researchField?.code
                ? t(`researchFields.${item?.researchField?.code}`)
                : item?.researchField?.name}
            </div>
            <div className="font-semibold mr-2 ">
              {t('projectProposal.general.majorsOrMajorsGroup.researchLine')}:{' '}
            </div>
            <div className="flex-1">{item?.researchLine}</div>
          </div>
        ))}
      {generalInfoData?.recommendedExperts?.length > 0 && (
        <RecommendedExpertsFields
          data={generalInfoData?.recommendedExperts}
          isLoading={isLoading}
        />
      )}

      {/* -researchType */}
      <div className="text-lg mt-3">
        <h4>{t('projectProposal.general.researchType.label')}</h4>
        <div className="flex">
          <p className="ml-5 w-28rem" style={{ marginRight: '150px' }}>
            <span className="font-semibold">
              {t('projectProposal.general.researchType.researchType')}:{' '}
            </span>
            {generalInfoData?.researchType?.code
              ? t(`researchTypes.${generalInfoData?.researchType?.code}`)
              : generalInfoData?.researchType?.name}
          </p>
          <p>
            <span className="font-semibold">{t('missionTypesForReviews.title')}: </span>
            {generalInfoData?.missionType
              ? t(`missionTypesForReviews.${generalInfoData?.missionType}`)
              : generalInfoData?.missionType}
          </p>
        </div>
      </div>

      {/* -researchTime  */}
      <div className="text-lg mt-2">
        <h4>{t('projectProposal.general.researchTime.label')}</h4>
        <div className="flex mb-1 ml-5">
          <div className="mr-2 font-semibold">
            {t('projectProposal.general.researchTime.researchTime')}:
          </div>
          <div className="flex-1">{generalInfoData?.implementationMonths}</div>
        </div>
        <div className="flex mb-1 ml-5">
          <div className="mr-2 font-semibold">
            {t('projectProposal.general.researchTime.yearProposed')}:{' '}
          </div>
          <div className="flex-1">{generalInfoData?.yearProposed}</div>
        </div>
      </div>

      {/* -totalExpenditure */}
      {!is1593(template) && (
        <ExpenditureSection
          expenditureData={expenditureData}
          expenditureInGeneral={expenditureInGeneral}
          generalInfoData={generalInfoData}
        />
      )}
      <h4 className="mr-2 mb-1">{t('projectProposal.general.implementationResources.label')}</h4>

      {/* ownerSection */}
      <OwnerSection owner={owner} generalInfoData={generalInfoData} />

      {/* scientificSecretarySection */}
      {is1593(template) && (
        <SecretarySection
          data={
            generalInfoData?.projectProposalContacts.filter((item) =>
              item?.projectRole?.code?.toLowerCase().includes('secretary')
            )?.[0] || {}
          }
        />
      )}

      {is1593(template) && (
        <div className="mb-2">
          <div className="mb-2">
            <span className="font-semibold">
              {t('projectProposal.general.ownerSection.owner.summarizedExperience')}:{' '}
            </span>
          </div>
          <RichTextViewer
            style={{ minHeight: '200px', width: '100%' }}
            value={owner?.experience ?? generalInfoData?.experience}
            showHeader={false}
            readOnly
          />
        </div>
      )}

      {/* A7 */}
      <div className="flex text-lg mt-2 flex-column">
        <h4>{t('projectProposal.general.leadingAgency.label')}</h4>
        {generalInfoData?.projectProposalOrganizations
          ?.filter((org) => org.type === 'LEAD')
          ?.map((org) => (
            <div className="flex ml-5 flex-column" key={org.id}>
              <div className="mb-2">
                <span className="font-semibold">
                  {t('projectProposal.general.organization.name')}:{' '}
                </span>
                <span>
                  {/* eslint-disable-next-line no-nested-ternary */}
                  {org?.englishName === 'Other'
                    ? org?.name
                    : t('lang') === 'vi'
                    ? org?.name
                    : org?.englishName}
                </span>
              </div>
              <div className="mb-2">
                <span className="font-semibold">
                  {t('projectProposal.general.organization.chief')}:{' '}
                </span>
                <span>{org?.chief}</span>
              </div>
              <div className="flex flex-wrap">
                <div className="w-30rem mb-2">
                  <span className="font-semibold">
                    {t('projectProposal.general.organization.phone')}:{' '}
                  </span>
                  <span>{org?.phone}</span>
                </div>
                <div className="w-20rem mb-2">
                  <span className="font-semibold">
                    {t('projectProposal.general.organization.fax')}:{' '}
                  </span>
                  <span>{org?.fax}</span>
                </div>
              </div>
              <div className="mb-2">
                <span className="font-semibold">
                  {t('projectProposal.general.organization.email')}:{' '}
                </span>
                <span>{org?.email}</span>
              </div>
              <div className="flex flex-wrap">
                <div className="w-30rem mb-2">
                  <span className="font-semibold">
                    {t('projectProposal.general.organization.bankAccount')}:{' '}
                  </span>
                  <span>{org?.bankAccount}</span>
                </div>
                <div className="mb-2">
                  <span className="font-semibold">
                    {t('projectProposal.general.organization.bank')}:{' '}
                  </span>
                  <span>{org?.bank}</span>
                </div>
              </div>
            </div>
          ))}
      </div>

      {/* A8 */}
      <div className="flex text-lg mt-2 flex-column">
        <h4>{t('projectProposal.general.partners.label')}</h4>
        {partners?.length > 0 ? (
          partners.map((org, index) => (
            <div className="flex ml-5 mb-2 flex-column" key={org.id}>
              <div className="mb-2">
                <span className="font-bold">
                  {t('projectProposal.general.organization.name')} {index + 1}:{' '}
                </span>
                <span>
                  {/* eslint-disable-next-line no-nested-ternary */}
                  {org?.englishName === 'Other'
                    ? org?.name
                    : t('lang') === 'vi'
                    ? org?.name
                    : org?.englishName}
                </span>
              </div>
              <div className="mb-2">
                <span className="font-semibold">
                  {t('projectProposal.general.partners.confirmationDocument')}:{' '}
                </span>
                {org?.partnerConfirmationUrl && (
                  <span
                    className="p-link text-primary underline"
                    onClick={() => handleDownloadFile(org?.partnerConfirmationUrl)}
                  >
                    {t('projectProposal.general.partners.attachment')}
                  </span>
                )}
              </div>
              <div className="mb-2">
                <span className="font-semibold">
                  {t('projectProposal.general.organization.chief')}:{' '}
                </span>
                <span>{org?.chief}</span>
              </div>
              <div className="flex flex-wrap">
                <div className="w-30rem mb-2">
                  <span className="font-semibold">
                    {t('projectProposal.general.organization.phone')}:{' '}
                  </span>
                  <span>{org?.phone}</span>
                </div>
                <div className="w-20rem mb-2">
                  <span className="font-semibold">
                    {t('projectProposal.general.organization.fax')}:{' '}
                  </span>
                  <span>{org?.fax}</span>
                </div>
              </div>
              <div className="mb-2">
                <span className="font-semibold">
                  {t('projectProposal.general.organization.address')}:{' '}
                </span>
                <span>{org?.address}</span>
              </div>
              <div
                className={
                  index === generalInfoData.projectProposalOrganizations.length - 2 ? '' : 'mb-3'
                }
              >
                <div>
                  <span className="font-semibold">
                    {t('projectProposal.general.organization.partnerContribution')}:{' '}
                  </span>
                </div>
                <p>{org?.partnerContribution}</p>
              </div>
            </div>
          ))
        ) : (
          <span>({t('projectProposal.general.partners.noPartner')})</span>
        )}
      </div>

      {/* A9 */}
      <div className="flex align-items-center">
        <h4>{t('projectProposal.general.researchHumanResources.label')}</h4>
        {personnelAdjustments?.length > 0 && (
          <span
            className="download-compare-file pointer-events-auto ml-2 cursor-pointer"
            onClick={() => setViewHistoricalScientificProfileVisible(true)}
          >
            <i
              className="view-icon pi pi-eye"
              data-pr-tooltip={t('projectProposal.viewHistoricalPersonnelAdjustment.view')}
              data-pr-position="right"
              style={{ color: 'var(--cyan-600)', fontSize: 15 }}
            />
            <Tooltip target=".view-icon" />
          </span>
        )}
      </div>

      <OwnerFields
        owners={generalInfoData?.projectProposalContacts
          ?.sort((a, b) => a.order - b.order)
          ?.filter((item) => item?.projectRole?.code?.toLowerCase().includes('owner'))}
        isLoading={isLoading}
        formatIndex={formatIndex}
        formatFullname={formatFullname}
        formatGender={formatGender}
        formatResearcherOrganization={formatResearcherOrganization}
      />
      <ScientificSecretaryFields
        scientificSecretaries={generalInfoData?.projectProposalContacts
          ?.sort((a, b) => a.order - b.order)
          .filter((item) => item?.projectRole?.code?.toLowerCase().includes('secretary'))}
        isLoading={isLoading}
        formatIndex={formatIndex}
        formatFullname={formatFullname}
        formatGender={formatGender}
        formatResearcherOrganization={formatResearcherOrganization}
      />
      <MainParticipantFields
        mainParticipants={generalInfoData?.projectProposalContacts
          ?.sort((a, b) => a.order - b.order)
          ?.filter((item) => item?.projectRole?.code?.toLowerCase().includes('main'))}
        isLoading={isLoading}
        formatIndex={formatIndex}
        formatFullname={formatFullname}
        formatGender={formatGender}
        formatResearcherOrganization={formatResearcherOrganization}
      />
      <ParticipantFields
        participants={generalInfoData?.projectProposalContacts
          ?.sort((a, b) => a.order - b.order)
          .filter((item) => item?.projectRole?.code?.toLowerCase() === 'participant')}
        isLoading={isLoading}
        formatIndex={formatIndex}
        formatFullname={formatFullname}
        formatGender={formatGender}
        formatResearcherOrganization={formatResearcherOrganization}
      />
      <TechnicianFields
        technicians={generalInfoData?.projectProposalContacts
          ?.sort((a, b) => a.order - b.order)
          ?.filter(
            (item) =>
              item?.projectRole?.code?.toLowerCase().includes('technician') ||
              item?.projectRole?.code?.toLowerCase().includes('support')
          )}
        isLoading={isLoading}
        formatIndex={formatIndex}
        formatFullname={formatFullname}
        formatGender={formatGender}
        formatResearcherOrganization={formatResearcherOrganization}
      />
      <DomesticExpertFields
        experts={generalInfoData?.projectProposalContacts
          ?.sort((a, b) => a.order - b.order)
          ?.filter((item) => item?.projectRole?.code?.toLowerCase().includes('domestic'))}
        isLoading={isLoading}
        formatIndex={formatIndex}
        formatFullname={formatFullname}
        formatGender={formatGender}
        formatResearcherOrganization={formatResearcherOrganization}
      />
      <InternationalExpertFields
        experts={generalInfoData?.projectProposalContacts
          ?.sort((a, b) => a.order - b.order)
          .filter((item) => item?.projectRole?.code?.toLowerCase().includes('international'))}
        isLoading={isLoading}
        formatIndex={formatIndex}
        formatFullname={formatFullname}
        formatGender={formatGender}
        formatResearcherOrganization={formatResearcherOrganization}
      />

      <ViewHistoricalPersonnelAdjustment
        visible={viewHistoricalScientificProfileVisible}
        setVisible={setViewHistoricalScientificProfileVisible}
        generalInfoData={personnelAdjustments}
        isLoading={isPersonnelAdjustmentsLoading}
        formatIndex={formatIndex}
        formatFullname={formatFullname}
        formatGender={formatGender}
        formatResearcherOrganization={formatResearcherOrganizationHistory}
      />
    </div>
  );
}

GeneralInfo.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  generalInfoData: PropTypes.object.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  expenditureData: PropTypes.object.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  expenditureInGeneral: PropTypes.object.isRequired,
  isLoading: PropTypes.bool.isRequired,
  handleDownloadFile: PropTypes.func.isRequired,
  handleDownloadScientificProfileFile: PropTypes.func.isRequired,
};
